import { observer } from 'mobx-react-lite'
import { Lock as LockIcon, Plus, EditPencil } from 'iconoir-react'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { LearnMoreLink } from 'app/components/shared/LearnMoreLink'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import SimpleTable from 'app/components/table/SimpleTable'
import { ActionsCell } from 'app/components/table/ActionsCell'
import { formatItemCount } from 'app/utils/formatItemCount'

export type RoleRow = {
  id?: string
  name?: string
  permissions?: string[]
  workspaceId?: string
  isDefault?: boolean
  usersAssigned?: number
}

export type RolesShadcnComponentProps = {
  roles: RoleRow[] | undefined
  canManageRoles: boolean
  defaultRoleNames: string[]
  onCreateRole: () => void
  onEditRole: (role: RoleRow) => void
  renderUsersAssigned?: (roleId: string) => React.ReactNode
}

function getPermissionCount(permissions: string[], workspaceId: string | undefined, type: 'pages' | 'features') {
  const pagePrefix = !workspaceId ? `org:pages:` : `pages:`
  const filtered =
    type === 'pages'
      ? permissions.filter((p) => p.startsWith(pagePrefix))
      : permissions.filter((p) => !p.startsWith(pagePrefix))
  const label = type === 'pages' ? 'page' : 'permission'
  return filtered.length > 0
    ? formatItemCount(filtered.length, label)
    : type === 'pages'
      ? `All Pages`
      : `All Permissions`
}

export const RolesShadcnComponent = observer(function RolesShadcnComponent({
  roles,
  canManageRoles,
  defaultRoleNames,
  onCreateRole,
  onEditRole,
}: RolesShadcnComponentProps) {
  const columns = useMemo(
    (): ColumnDef<RoleRow, any>[] => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        meta: {
          defaultVisible: true,
          sortKey: `name.keyword`,
          ControlledComponent: (row?: RoleRow) => {
            if (!row) return <div />
            const isOwnerRole = row.name === `Owner` || row.name === `Org Owner`
            const isDefaultRole = defaultRoleNames.includes(row.name!)
            const isEditable = canManageRoles && !isOwnerRole
            return (
              <ActionsCell
                content={
                  <span className="flex items-center gap-2">
                    <span className={isOwnerRole ? `text-subtle` : ``}>{row.name}</span>
                    {isDefaultRole && (
                      <span className="shrink-0 rounded bg-surface-muted px-1.5 py-0.5 text-xs text-subtle">
                        Default Role
                      </span>
                    )}
                    {isOwnerRole && <LockIcon className="shrink-0 text-minimal" width={14} height={14} />}
                  </span>
                }
                onClick={isEditable ? () => onEditRole(row) : undefined}
                actions={
                  isEditable ? (
                    <div className="flex h-6 w-6 items-center justify-center rounded border border-strong bg-surface-subtle p-1.5">
                      <EditPencil className="text-strong" />
                    </div>
                  ) : undefined
                }
              />
            )
          },
        },
      },
      {
        id: `usersAssigned`,
        accessorKey: `usersAssigned`,
        header: `Users Assigned`,
        enableSorting: false,
        minSize: 140,
        meta: { defaultVisible: true },
        cell: ({ row }) => {
          const val = row.original.usersAssigned
          return val != null ? formatItemCount(val, `user`) : `—`
        },
      },
      {
        id: `pageAccess`,
        accessorFn: (row) => getPermissionCount(row.permissions || [], row.workspaceId, 'pages'),
        header: `Page Access`,
        enableSorting: false,
        minSize: 140,
        meta: { defaultVisible: true },
      },
      {
        id: `permissions`,
        accessorFn: (row) => getPermissionCount(row.permissions || [], row.workspaceId, 'features'),
        header: `Permissions`,
        enableSorting: false,
        minSize: 140,
        meta: { defaultVisible: true },
      },
    ],
    [canManageRoles, defaultRoleNames, onEditRole],
  )

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeaderComponent
          title="Roles"
          primaryButtonSlot={
            canManageRoles ? (
              <Button variant="primary" size="lg" onClick={onCreateRole}>
                <Plus strokeWidth={2} width={16} height={16} />
                Add New Role
              </Button>
            ) : undefined
          }
          learnMore={
            <LearnMoreLink
              className="h-10 pb-1.5"
              to="https://intercom.help/trackable/en/articles/11643458-user-permissions"
            >
              Learn About Roles
            </LearnMoreLink>
          }
        />
      }
    >
      <SimpleTable data={roles} columns={columns} enableExport={false} hideFooter edgePadded />
    </PageScroll>
  )
})
